"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import EditableList from "@/app/(components)/generics/EditableList/EditableList";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { useFetchAvatarPain } from "@/app/hooks/fetchs/client-avatar/useFetchAvatarTabs";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AvatarField, UserClientAvatar } from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { Limits } from "@/app/vars/Limits";
import { AvatarQuerySubKey, QueryKey } from "@/app/vars/QueryKeys";
import styles from "../AvatarDetail.module.css";
import { AvatarDetailCard } from "./AvatarDetailCard/AvatarDetailCard";
import cardStyles from "./AvatarDetailCard/AvatarDetailCard.module.css";

type Props = DefaultProps & {
  avatar: UserClientAvatar;
  isLoading?: boolean;
  isAnalyzing?: boolean;
  params: { lang: string };
};

const listPlaceholder = [
  { cells: [{ width: "90%" }] },
  { cells: [{ width: "75%" }] },
  { cells: [{ width: "60%" }] },
];

export default function AvatarDetailPain(props: Props) {
  const T = useT();
  const canEdit = useCanEditContent();
  const { avatar, isLoading, isAnalyzing = false, params } = props;
  const user = useAuthUser();
  const isPlaceholder = !user;
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: painData, isLoading: isFetching } = useFetchAvatarPain({
    avatarId: avatar.id,
    enabled: !isPlaceholder,
  });

  const fullAvatar = { ...avatar, ...(painData || {}) } as UserClientAvatar;
  const isLoadingTab = isLoading || isFetching;

  const handleSaveList = async (fieldKey: AvatarField, newItems: string[]) => {
    try {
      await updateAvatarField(avatar.offerId, fieldKey, newItems);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.PAIN,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  const handleSaveText = async (fieldKey: AvatarField, val: string) => {
    try {
      await updateAvatarField(avatar.offerId, fieldKey, val);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.PAIN,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  if (isLoadingTab) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader
          centered
          delay={Limits.CLIENT_AVATAR_LOADER_DELAY}
          color="var(--color-green-3)"
        />
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_DESIRES)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.desires || []}
              onSave={(items) => handleSaveList(AvatarField.DESIRES, items)}
              bulletColor="var(--color-analysis-signal)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_FRUSTRATIONS)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.frustrations || []}
              onSave={(items) => handleSaveList(AvatarField.FRUSTRATIONS, items)}
              bulletColor="var(--color-analysis-pain)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_DREAMS)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.dreams || []}
              onSave={(items) => handleSaveList(AvatarField.DREAMS, items)}
              bulletColor="var(--color-analysis-signal)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_FEARS)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.fears || []}
              onSave={(items) => handleSaveList(AvatarField.FEARS, items)}
              bulletColor="var(--color-analysis-pain)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <AvatarDetailCard
        title={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_COST_TODAY)}
        isPlaceholder={isPlaceholder}
        placeholderConfig={[{ cells: [{ width: "50%" }] }, { cells: [{ width: "85%" }] }]}
      >
        <div className={styles.costsGrid}>
          <div className={styles.costItem}>
            <p className={styles.costLabel}>{T(TKey.CLIENT_AVATAR_DETAIL_PAIN_COST_TIME)}</p>
            {isPlaceholder ? null : (
              <EditableText
                value={fullAvatar.costTime || ""}
                onSave={(val) => handleSaveText(AvatarField.COST_TIME, val)}
                className={cardStyles.textBody}
                multiline={true}
                maxLength={Limits.MAX_AVATAR_SENTENCE}
                placeholder={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_COST_TIME_PLACEHOLDER)}
                disabled={isAnalyzing || !canEdit}
              />
            )}
          </div>
          <div className={styles.costItem}>
            <p className={styles.costLabel}>{T(TKey.CLIENT_AVATAR_DETAIL_PAIN_COST_ENERGY)}</p>
            {isPlaceholder ? null : (
              <EditableText
                value={fullAvatar.costEnergy || ""}
                onSave={(val) => handleSaveText(AvatarField.COST_ENERGY, val)}
                className={cardStyles.textBody}
                multiline={true}
                maxLength={Limits.MAX_AVATAR_SENTENCE}
                placeholder={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_COST_ENERGY_PLACEHOLDER)}
                disabled={isAnalyzing || !canEdit}
              />
            )}
          </div>
          <div className={styles.costItem}>
            <p className={styles.costLabel}>{T(TKey.CLIENT_AVATAR_DETAIL_PAIN_COST_OPPORTUNITY)}</p>
            {isPlaceholder ? null : (
              <EditableText
                value={fullAvatar.costOpportunity || ""}
                onSave={(val) => handleSaveText(AvatarField.COST_OPPORTUNITY, val)}
                className={cardStyles.textBody}
                multiline={true}
                maxLength={Limits.MAX_AVATAR_SENTENCE}
                placeholder={T(TKey.CLIENT_AVATAR_DETAIL_PAIN_COST_OPPORTUNITY_PLACEHOLDER)}
                disabled={isAnalyzing || !canEdit}
              />
            )}
          </div>
        </div>
      </AvatarDetailCard>

      {isPlaceholder && (
        <div className={styles.ctaContainer}>
          <Button
            min={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_CREATE_BUTTON)}
            onClick={() => ModalStore.getState().openSignIn(params)}
          />
        </div>
      )}
    </div>
  );
}
